import type { Messages } from './messages';

/**
 * The Nynorsk catalogue (ADR-031) — `en.ts` key for key, typed as `Messages` so a missing or
 * an extra key is a compile error. Norwegian typography throughout: «…» where English has “…”,
 * a spaced en dash " – " where English has " — ", sentence case, no trailing period on a
 * button, the informal "du". The 2012 norm (Nynorskordboka) with the e-infinitive, and the
 * glossary of the design spec as it stands (delplan, gjeremålsliste, oppgåve, Skriveverna,
 * Byt lenkje, Gje nytt namn, Lastar …, øvste nivå, startsida).
 *
 * Identical to English on purpose, and nothing else: the brand "Planlets" (`app.title`, the
 * `signIn.installLine` footer, whose identity string is never translated) and the arrows
 * `node.up` / `node.down`. The block-type ids become real words here (`gjeremålsliste`,
 * `oppgåve`, `notat`); a type not listed falls back to its raw id like in every locale.
 */
export const nn: Messages = {
  resources: {
    snapshotReferencesHelp: "Eit augneblinksbilete krev at alle inkluderte referansar er offentleg tilgjengelege.",
    willPublish: "Vert offentleg",
    keptPrivate: "Vert halde privat",
    loading: "Lastar ressursar…",
    catalogue: "Min katalog",
    intro: "Presentasjonar, oppgåver og data, samla på éin stad.",
    shared: "Delte ressursar",
    empty: "Ingen ressursar her enno.",
    contents: "Innhald",
    name: "Ressursnamn",
    kind: "Ressurstype",
    create: "Legg til ressurs",
    example: "Prøv eit verksemdsdøme",
    exampleHelp: "Opprettar ein privat presentasjon med fiktive kvartalstal, føresetnader og utrekningar.",
    address: "URN for ressursen",
    open: "Opne ressurs",
    copy: "Kopier adresse",
    copied: "Adressa er kopiert",
    copyFailed: "Merk og kopier adressa ovanfor.",
    back: "Tilbake til katalogen",
    parent: "Opne overordna ressurs",
    edit: "Rediger innhald",
    save: "Lagre innhald",
    cancel: "Avbryt",
    body: "Tekst",
    completed: "Fullført",
    events: "Kalenderhendingar",
    eventsHelp: "Éi hending per linje: ÅÅÅÅ-MM-DD, tittel.",
    unit: "Eining",
    frequency: "Periodeinndeling",
    data: "Tidsserie som CSV",
    dataHelp: "To kolonnar: period,value. Bruk 2026-Q1, 2026-01, ÅÅÅÅ-MM-DD eller value for ein skalar. Tom verdi tyder manglande data.",
    importCsv: "Importer CSV-fil",
    derived: "Rekn ut frå inndata",
    expression: "Formel",
    inputs: "Namngjevne inndata",
    inputsHelp: "Éi inndatakjelde per linje: revenue = urn:uuid:…",
    formulaHelp: "Bruk +, −, *, /, parentesar, sum(series), forecast(history, growth, quarters) eller align(series, template). Utrekningar køyrer på tenaren.",
    reference: "URN til målressurs",
    follow: "Opne referert ressurs",
    live: "Løpande",
    snapshot: "Augneblinksbilete",
    calculated: "Sist utrekna",
    captured: "Tidspunkt for opptak",
    notCalculated: "Ikkje rekna ut enno",
    unavailable: "Dette innhaldet er utilgjengeleg. Oppdater for å prøve igjen.",
    period: "Periode",
    value: "Verdi",
    missing: "Manglar",
    refresh: "Oppdater",
    publish: "Publiser",
    publishLive: "Publiser løpande",
    publishSnapshot: "Publiser augneblinksbilete",
    liveHelp: "Den offentlege adressa vert verande. Opne eller oppdater ressursen for å sjå dei nyaste inndataverdiane.",
    snapshotHelp: "Opprett ein eigen offentleg ressurs med fast innhald og utrekna verdiar. Han får si eiga adresse.",
    publicationBlocked: "Kan ikkje publisere: ei utrekning kan ikkje utførast eller bruker andre sine private data. Publisering av resultat frå andre sine private inndata er ikkje tillate enno.",
    previewHelp: "Underressursar følgjer synlegheitsinnstillingane sine. Referansar gjev ikkje tilgang til målressursane.",
    unavailableReferences: "Referansar offentlege sjåarar ikkje kan opne",
    unpublish: "Avpubliser",
    permissions: "Tilgangsrettar",
    inheritance: "Underressursar arvar tilgang med mindre han vert overstyrt. Referansar held på eigne rettar.",
    move: "Flytt ressurs",
    destination: "URN til ny overordna ressurs",
    delete: "Slett ressurs",
    deleteHelp: "Underressursar vert òg sletta. Du kan angre frå katalogen din.",
    undo: "Angre sletting",
    deleted: "Ressurs sletta",
    badCsv: "Bruk nøyaktig to CSV-kolonnar og endelege tal. Periodar må vere unike.",
    badInputs: "Bruk eit unikt namn = urn:uuid:… på kvar linje.",
    badEvents: "Bruk ein gyldig dato og tittel på kvar kalenderrad.",
    fileFailed: "Fila kunne ikkje lesast. Lim inn innhaldet i CSV-feltet.",
    snapshotImmutable: "Dette augneblinksbiletet har fast innhald. Synlegheit og rettar kan framleis endrast.",
    rename: "Gje ressursen nytt namn",
    unknownType: "Denne ressurstypen har ingen nettredigerar enno.",
    types: {
      "catalogue": "Katalog",
      "presentation": "Presentasjon",
      "list": "Liste",
      "task": "Oppgåve",
      "calendar": "Kalender",
      "time-series": "Tidsserie",
      "reference": "Referanse",
      "note": "Notat",
    } as Record<string, string>,
    frequencies: {
      quarterly: "Kvartalsvis",
      monthly: "Månadleg",
      daily: "Dagleg",
      scalar: "Enkeltverdi",
    },
  },
  app: {
    title: 'Planlets',
    tagline: 'Komponerbare planleggingskomponentar – bygg planar av delbare blokker.',
    signedInWithoutEmail: 'Logga inn',
    signedIn: (email: string) => `Logga inn som ${email}`,
    signOut: 'Logg ut',
    dismiss: 'Lukk',
    sessionExpired: 'Økta di har gått ut – logg inn igjen.',
    sessionExpiredWith: (name: string) =>
      `Økta di har gått ut – logg inn igjen for å halde fram med «${name}».`,
    signInFailed: 'Innlogginga mislukkast. Prøv igjen.',
    deleteTitle: (name: string) => `Slette «${name}»?`,
    deleteMessage: 'Du kan angre dette frå startsida.',
    delete: 'Slett',
    importSuffix: (name: string) => `${name} (min)`,
    newTaskTitle: 'Ny oppgåve',
  },

  signIn: {
    heading: 'Logg inn',
    lead: 'Planlets brukar plattformkontoen din – vel korleis du vil logge inn.',
    withProvider: (provider: string) => `Logg inn med ${provider}`,
    installLabel: 'installasjon',
    installLine: (install: string) => `Planlets ${install}`,
  },

  home: {
    deleted: (name: string) => `Sletta «${name}».`,
    undo: 'Angre',
    heading: 'Planane mine',
    loading: 'Lastar planane dine …',
    empty: 'Ingen planar enno – opprett den første nedanfor.',
    roleFor: (name: string) => `rolle for ${name}`,
    refresh: 'Oppdater',
  },

  openPlan: {
    createHeading: 'Opprett ein plan',
    nameLabel: 'Plannamn',
    namePlaceholder: 't.d. Sommartur',
    create: 'Opprett',
    openHeading: 'Opne ein plan',
    idLabel: 'Plan-id',
    idPlaceholder: 'lim inn ein plan-id',
    open: 'Opne',
  },

  header: {
    planVersion: 'planversjon',
    yourAccess: 'tilgangen din',
    rename: 'Gje nytt namn',
    share: 'Del',
    move: 'Flytt',
    delete: 'Slett',
    makeItMine: 'Gjer han til min',
    close: 'Lukk',
    newPlanName: 'nytt plannamn',
    save: 'Lagre',
    cancel: 'Avbryt',
  },

  access: {
    owner: 'Eigar',
    write: 'Kan redigere',
    read: 'Skriveverna',
    publicRead: 'Skriveverna (offentleg)',
    none: 'Ingen tilgang',
  },

  role: {
    owner: 'Eigar',
    writer: 'Kan redigere',
    reader: 'Skriveverna',
    none: 'Ingen tilgang',
  },

  editor: {
    blockType: 'blokktype',
    blockTitle: 'blokktittel',
    titlePlaceholder: 'Kva planlegg du?',
    addBlock: 'Legg til blokk',
    emptyEditable: 'Ingen blokker enno – legg til den første ovanfor.',
    emptyReadOnly: 'Ingen blokker enno.',
  },

  blockType: {
    'todo-list': 'gjeremålsliste',
    task: 'oppgåve',
    note: 'notat',
  },

  childPlans: {
    heading: 'Delplanar',
    loading: 'Lastar delplanar …',
    empty: 'Ingen delplanar.',
    accessTo: (name: string) => `tilgang til ${name}`,
    nameLabel: 'namn på delplan',
    namePlaceholder: 't.d. Pakkeliste',
    add: 'Legg til delplan',
  },

  node: {
    up: '↑',
    down: '↓',
    renameButton: 'Gje nytt namn',
    addInsideButton: '+ Legg til inni',
    removeButton: 'Fjern',
    save: 'Lagre',
    cancel: 'Avbryt',
    moveUp: (title: string) => `flytt ${title} opp`,
    moveDown: (title: string) => `flytt ${title} ned`,
    rename: (title: string) => `gje ${title} nytt namn`,
    addInside: (title: string) => `legg til inni ${title}`,
    remove: (title: string) => `fjern ${title}`,
    newTitleFor: (title: string) => `ny tittel på ${title}`,
  },

  confirm: {
    cancel: 'Avbryt',
  },

  share: {
    heading: 'Del',
    headingFor: (name: string) => `Del «${name}»`,
    done: 'Ferdig',
    loading: 'Lastar delingsinnstillingar …',
    visibility: 'Synlegheit',
    publicMode: 'offentleg modus',
    mode: {
      inherit: 'Arv frå overordna',
      private: 'Privat',
      public: 'Offentleg – alle med lenkja',
    },
    linkLabel: 'Delingslenkje',
    shareLink: 'delingslenkje',
    rotateLink: 'Byt lenkje',
    people: 'Personar',
    nobodyElse: 'Ingen andre har tilgang.',
    roleFor: (email: string) => `rolle for ${email}`,
    grantRoles: {
      reader: 'Kan lese',
      writer: 'Kan redigere',
      none: 'Blokkert',
    },
    remove: (email: string) => `fjern ${email}`,
    removeButton: 'Fjern',
    grantEmail: 'e-post for tilgang',
    emailPlaceholder: 'namn@example.com',
    grantRole: 'rolle for tilgang',
    add: 'Legg til',
  },

  move: {
    heading: (name: string) => `Flytt «${name}»`,
    loading: 'Lastar plasseringar …',
    currentLocation: 'noverande plassering',
    atTopLevel: 'På øvste nivå.',
    under: (name: string) => `Under «${name}».`,
    noOtherPlans: 'Du eig ingen andre planar å flytte denne inn i.',
    moveTo: 'Flytt til',
    destination: 'ny plassering',
    topLevel: 'Øvste nivå (ingen overordna)',
    move: 'Flytt',
    cancel: 'Avbryt',
  },

  sharedViewer: {
    sharedView: 'delt vising',
    backToRoot: 'Tilbake til den delte planen',
    loading: 'Lastar …',
    planVersion: 'planversjon',
    empty: 'Ingenting her enno.',
    subPlanlets: 'Delplanar',
    noPublicSubPlanlets: 'Ingen offentlege delplanar.',
  },

  todo: {
    addTask: 'Legg til oppgåve',
    newTask: 'ny oppgåve',
    addPlaceholder: 'Legg til ei oppgåve',
    empty: 'Ingen oppgåver enno.',
    save: 'Lagre',
    cancel: 'Avbryt',
    list: (title: string) => `gjeremålsliste ${title}`,
    listMoveUp: (title: string) => `flytt lista ${title} opp`,
    listMoveDown: (title: string) => `flytt lista ${title} ned`,
    listRename: (title: string) => `gje lista ${title} nytt namn`,
    listNewTitle: (title: string) => `ny tittel på lista ${title}`,
    listRemove: (title: string) => `fjern lista ${title}`,
    done: (title: string) => `${title} fullført`,
    rename: (title: string) => `gje ${title} nytt namn`,
    newTitle: (title: string) => `ny tittel på ${title}`,
    moveUp: (title: string) => `flytt ${title} opp`,
    moveDown: (title: string) => `flytt ${title} ned`,
    remove: (title: string) => `fjern ${title}`,
  },

  errors: {
    unreachable: 'Tenesta er ikkje tilgjengeleg. Prøv igjen.',
    maxNesting: (description: string) => `Maksimalt tal på nivå nådd – ${description}`,
  },

  auth: {
    parFailed: (status: string) => `Innlogginga kunne ikkje starte (PAR ${status}).`,
    noneInProgress: 'Inga innlogging pågår – start på nytt.',
    stateMismatch: 'Innloggingssvaret høyrer ikkje til denne nettlesarøkta.',
    tokenFailed: (status: string) => `Innlogginga kunne ikkje fullførast (token ${status}).`,
  },

  unsupported: {
    'insecure-context': 'Denne WebView-en køyrer ikkje appen i ein sikker kontekst.',
    'no-webcrypto': 'Denne WebView-en manglar WebCrypto – oppdater Android System WebView.',
    'no-random-uuid':
      'Denne WebView-en er for gammal (manglar crypto.randomUUID) – ' +
      'oppdater Android System WebView.',
  },

  language: {
    label: 'Språk',
  },

  /** The Grove navigation, appearance settings, and supporting interface copy. */
  grove: {
    headerNote: 'Rom for å kome vidare.',
    appearance: 'Utsjånad',
    system: 'Følg eininga',
    light: 'Lyst',
    dark: 'Mørkt',
    workspace: 'Arbeidsområde',
    plans: 'Planar',
    shared: 'Delt',
    settings: 'Innstillingar',
    sidebarNote: 'Éin stad. Mange moglegheiter.',
    skipContent: 'Hopp til innhaldet',
    settingsEyebrow: 'Gjer deg heime',
    settingsDescription: 'Nokre val som gjer dette til din eigen stad.',
    appearanceDescription: 'Vel ein utsjånad, eller følg eininga. Valet ditt blir berre lagra på denne eininga.',
    colorMode: 'Fargemodus',
    account: 'Kontoen din',
    accountDescription: 'Bruk den same kontoen for å halde fram med planane dine på nett, iOS og Android.',
    planActions: 'Planhandlingar',
    actionsFor: (title: string) => `Handlingar for ${title}`,
    addTaskTo: (title: string) => `Legg til oppgåve i ${title}`,
    subplanDescription: 'Hald nærskylde planar samla.',
    subplanPlaceholder: 'Gje delplanen din eit namn',
    progressTitle: 'Framdrift i planen',
    progressSummary: (completed: number, total: number) => `${completed} av ${total} oppgåver fullførte`,
    completedTasks: 'Fullførte oppgåver',
    remainingTasks: (remaining: number) => `Står att: ${remaining}`,
    listComplete: (completed: number, total: number) => `${completed} / ${total} fullførte`,
    homeEyebrow: 'Din stad',
    homeDescription: 'Ein oversiktleg stad for prosjekta og kvardagsplanane dine.',
    sharedEyebrow: 'Saman om planane',
    sharedHeading: 'Delt med meg',
    sharedDescription: 'Planane andre har invitert deg til å lese eller arbeide med.',
    sharedLoading: 'Lastar delte planar …',
    sharedEmpty: 'Ingen delte planar enno. Planar som blir delte med kontoen din, blir viste her.',
    createDescription: 'Gje det neste prosjektet eller den private planen din ein stad å starte.',
    openDescription: 'Har du allereie ein plan-id? Hald fram der du slapp.',
    namePlaceholder: 't.d. Produktlansering eller sommartur',
    signinEyebrow: 'Litt struktur. Fleire moglegheiter.',
    signinHeadingStart: 'Gje plass til',
    signinHeadingEnd: 'det som kjem.',
    signinDescription: 'Samle prosjekt, felles oppgåver og private planar på éin oversiktleg stad.',
    planPrinciple: 'Start med ein plan.',
    planPrincipleDescription: 'Del han opp i overkomelege delar.',
    togetherPrinciple: 'Bygg han saman.',
    togetherPrincipleDescription: 'Gje dei rette personane tilgang.',
    movePrinciple: 'Kom vidare.',
    movePrincipleDescription: 'Eitt steg om gongen.',
    welcome: 'Velkomen til Planlets',
    signinAccount: 'Vel ein konto for å halde fram til planane dine.',
    accountNote: 'Bruk den same kontoen på nett, iOS og Android.',
    collaborate: 'Samarbeid',
    sharingDescription: 'Vel korleis planen blir delt, og kva kvar person kan gjere.',
    emailAddress: 'E-postadresse',
    permission: 'Tilgang',
    organize: 'Organiser',
    sharedNavigation: 'Navigasjon i delt plan',
    sharedReadOnly: 'Delt vising · Skriveverna',
    sharedPlan: 'Delt plan',
  },
};
